import {
  Add as AddIcon,
  CheckCircle as CompleteIcon,
  Dashboard as OverviewIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Star as FinalIcon,
  MoreHoriz as InProgressIcon,
  MoreVert as MoreVertIcon,
  List as LongListIcon,
  RadioButtonUnchecked as NotStartedIcon,
  CheckCircleOutline as ReadyIcon,
  Assignment as RequirementsIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem as MuiMenuItem,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ResearchTrackBasic } from "../../services/researchApi";
import { StageInfo, WorkflowStage } from "../../types/researchWorkflow";

/** Which section of the project is currently active. */
export type ProjectSection =
  | { kind: "overview" }
  | { kind: "track"; trackId: string; stage: WorkflowStage };

interface ResearchWorkflowSidebarProps {
  /** All tracks belonging to the current project. */
  tracks: ResearchTrackBasic[];
  /** Currently active section (overview or a specific track+stage). */
  activeSection: ProjectSection;
  /** Stage info for the currently loaded track (if any). */
  stages: {
    requirements: StageInfo;
    longList: StageInfo;
    shortList: StageInfo;
    finalSelection: StageInfo;
  };
  onSectionChange: (section: ProjectSection) => void;
  onAddTrack: () => void;
  onDeleteTrack: (trackId: string) => void;
  isViewer?: boolean;
}

const ResearchWorkflowSidebar: React.FC<ResearchWorkflowSidebarProps> = ({
  tracks,
  activeSection,
  stages,
  onSectionChange,
  onAddTrack,
  onDeleteTrack,
  isViewer = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTrackId, setMenuTrackId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTrackId, setDeleteTrackId] = useState<string | null>(null);

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    trackId: string,
  ) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTrackId(trackId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTrackId(null);
  };

  const handleDeleteClick = () => {
    setDeleteTrackId(menuTrackId);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (deleteTrackId) {
      onDeleteTrack(deleteTrackId);
    }
    setDeleteConfirmOpen(false);
    setDeleteTrackId(null);
  };
  // ── helpers ──────────────────────────────────────────────

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "complete":
        return <CompleteIcon sx={{ fontSize: 14, color: "success.main" }} />;
      case "ready":
        return <ReadyIcon sx={{ fontSize: 14, color: "info.main" }} />;
      case "in_progress":
        return <InProgressIcon sx={{ fontSize: 14, color: "warning.main" }} />;
      default:
        return <NotStartedIcon sx={{ fontSize: 14, color: "text.disabled" }} />;
    }
  };

  const getStageIcon = (stage: WorkflowStage) => {
    switch (stage) {
      case "requirements":
        return <RequirementsIcon sx={{ fontSize: 18 }} />;
      case "longList":
        return <LongListIcon sx={{ fontSize: 18 }} />;
      case "shortList":
        return <FilterListIcon sx={{ fontSize: 18 }} />;
      case "finalSelection":
        return <FinalIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getStageLabel = (stage: WorkflowStage) => {
    switch (stage) {
      case "requirements":
        return "Requirements";
      case "longList":
        return "Discovery";
      case "shortList":
        return "Evaluation";
      case "finalSelection":
        return "Final Selection";
    }
  };

  const getStatusLabel = ({ stage, status }: StageInfo) => {
    switch (status) {
      case "not_started":
        return "Not Started";
      case "in_progress":
        return stage === "shortList" ? "Evaluating" : "In Progress";
      case "ready":
        // Only the Evaluation stage produces this today: shortlisted
        // candidates whose evaluation has not run (or was cancelled).
        return stage === "shortList" ? "Awaiting Evaluation" : "Ready";
      case "complete":
        return "Complete";
      default:
        return null;
    }
  };

  const getCountBadge = (stageInfo: StageInfo) => {
    const { stage, counts } = stageInfo;
    switch (stage) {
      case "requirements":
        return null;
      case "longList":
        if (counts.total) {
          return `${counts.approved || 0}/${counts.total}`;
        }
        return null;
      case "shortList":
        // evaluated/shortlisted, mirroring Discovery's approved/total.
        if (counts.shortlisted) {
          return `${counts.evaluated || 0}/${counts.shortlisted}`;
        }
        return null;
      case "finalSelection":
        return counts.final || 0;
      default:
        return null;
    }
  };

  const stageKeys: WorkflowStage[] = [
    "requirements",
    "longList",
    "shortList",
    "finalSelection",
  ];

  const isOverviewActive = activeSection.kind === "overview";
  const activeTrackId =
    activeSection.kind === "track" ? activeSection.trackId : null;
  const activeStage =
    activeSection.kind === "track" ? activeSection.stage : null;

  // Accordion style matching SearchFilterPanel
  const accordionSx = {
    bgcolor: "background.paper",
    borderBottom: 1,
    borderColor: "divider",
    "&:before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
  };

  return (
    <Box
      sx={{
        width: 320,
        height: "100%",
        backgroundColor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Scrollable navigation content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {/* Project Overview accordion */}
        <Box sx={{ px: 1 }}>
          <Accordion
            sx={accordionSx}
            elevation={0}
            expanded={isOverviewActive}
            onChange={(_, expanded) => {
              if (expanded) {
                onSectionChange({ kind: "overview" });
              }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <OverviewIcon
                  sx={{
                    fontSize: 18,
                    color: isOverviewActive ? "primary.main" : "text.secondary",
                  }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={isOverviewActive ? 600 : "medium"}
                  color={isOverviewActive ? "primary.main" : "text.primary"}
                >
                  Project Overview
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: 1 }}>
              <Box
                onClick={() => onSectionChange({ kind: "overview" })}
                sx={{
                  mx: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  backgroundColor: isOverviewActive
                    ? "action.selected"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isOverviewActive
                      ? "action.selected"
                      : "action.hover",
                  },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={isOverviewActive ? 600 : 400}
                  color={isOverviewActive ? "primary.main" : "text.primary"}
                >
                  Overview
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Track accordions */}
        <Box sx={{ px: 1 }}>
          {tracks.map((track) => {
            const isExpanded = activeTrackId === track.id;

            return (
              <Accordion
                key={track.id}
                sx={accordionSx}
                elevation={0}
                expanded={isExpanded}
                onChange={(_, expanded) => {
                  if (expanded) {
                    // When expanding a track, default to the first stage
                    onSectionChange({
                      kind: "track",
                      trackId: track.id,
                      stage: activeStage || "requirements",
                    });
                  } else {
                    // Collapsing goes back to overview
                    onSectionChange({ kind: "overview" });
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={
                    !isViewer ? (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, track.id);
                        }}
                        sx={{
                          p: 0.25,
                          color: "text.secondary",
                          "&:hover": { color: "text.primary" },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : <ExpandMoreIcon />
                  }
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={isExpanded ? 600 : "medium"}
                      color={isExpanded ? "primary.main" : "text.primary"}
                    >
                      {track.name}
                      {track.status !== "active" && (
                        <Chip
                          label={track.status}
                          size="small"
                          sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ display: "block", lineHeight: 1.2 }}
                    >
                      {track.track_type === "find_competitors"
                        ? "Find Competitors"
                        : track.track_type === "find_customers"
                          ? "Find Customers & Leads"
                          : track.track_type === "find_partners"
                            ? "Find Partners & Suppliers"
                            : track.track_type}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, pb: 1 }}>
                  {stageKeys.map((stageKey) => {
                    const stageInfo = stages[stageKey];
                    const isStageActive =
                      isExpanded && activeStage === stageKey;
                    const countBadge = getCountBadge(stageInfo);

                    return (
                      <Box
                        key={stageKey}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSectionChange({
                            kind: "track",
                            trackId: track.id,
                            stage: stageKey,
                          });
                        }}
                        sx={{
                          mx: 1,
                          mb: 0.25,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1,
                          cursor: "pointer",
                          backgroundColor: isStageActive
                            ? "action.selected"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isStageActive
                              ? "action.selected"
                              : "action.hover",
                          },
                          transition: "all 0.15s",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              color: isStageActive
                                ? "primary.main"
                                : "text.secondary",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {getStageIcon(stageKey)}
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={isStageActive ? 600 : 400}
                            color={
                              isStageActive ? "primary.main" : "text.primary"
                            }
                            sx={{ flex: 1 }}
                          >
                            {getStageLabel(stageKey)}
                          </Typography>
                          {countBadge !== null && (
                            <Chip
                              label={countBadge}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                backgroundColor: isStageActive
                                  ? "primary.main"
                                  : "action.selected",
                                color: isStageActive
                                  ? "primary.contrastText"
                                  : "text.secondary",
                              }}
                            />
                          )}
                        </Box>
                        {/* Status indicator */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.25,
                            ml: 3.5,
                          }}
                        >
                          {getStatusIcon(stageInfo.status)}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {getStatusLabel(stageInfo)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        {/* Add Track button */}
        {!isViewer && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Button
              variant="text"
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddTrack}
              fullWidth
              sx={{
                justifyContent: "flex-start",
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              Add Research Track
            </Button>
          </Box>
        )}
      </Box>

      {/* Track context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { backgroundColor: "#1e1e1e" } } }}
      >
        <MuiMenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2" color="error">
              Delete Track
            </Typography>
          </ListItemText>
        </MuiMenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#1e1e1e" } }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          Delete Research Track?
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete the track and all its data including
            requirements, runs, evaluations, and entities. This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResearchWorkflowSidebar;
